import { IconName, IconSize } from '@ardoq/icons';
import { ViewIds } from '@ardoq/api-types';
import { createDropdownSliderOptionManager } from '@ardoq/settings-bar';
import {
  type ButtonConfig,
  type DropdownConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { CHANGE_DEPTH } from './consts';

const COLLAPSE_GROUPS_BASE_CONFIG = {
  id: 'collapseChildrenTree',
  iconName: IconName.COLLAPSE_GROUPS,
  iconSize: IconSize.LARGE,
};

type CollapseChildrenTreeLabels = {
  changeDepth: string;
  collapse: string;
  expand: string;
  noDepth: string;
};

const DEFAULT_LABELS: CollapseChildrenTreeLabels = {
  changeDepth: CHANGE_DEPTH,
  collapse: 'Collapse',
  expand: 'Expand',
  noDepth: 'There is no depth to collapse or expand',
};

const getDropdownSliderOption = (
  viewId: ViewIds,
  onViewSettingsUpdate: OnViewSettingsUpdate
) =>
  createDropdownSliderOptionManager(
    viewId,
    new Map(),
    onViewSettingsUpdate,
    false
  );

type CollapseChildrenTreeArgs = {
  viewId: ViewIds;
  treeDepth: number;
  maxTreeDepth: number;
  canSetTreeDepth: boolean;
  hasCollapsedNodes: boolean;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  labels?: Partial<CollapseChildrenTreeLabels>;
};

type ResolvedCollapseChildrenTreeArgs = Omit<
  CollapseChildrenTreeArgs,
  'labels'
> & {
  labels: CollapseChildrenTreeLabels;
};

const collapseChildrenTreeButton = ({
  viewId,
  treeDepth,
  canSetTreeDepth,
  onViewSettingsUpdate,
  labels,
}: ResolvedCollapseChildrenTreeArgs): ButtonConfig => {
  const isDisabled = !canSetTreeDepth;
  const cappedTreeDepth = Math.min(treeDepth, 2);
  return {
    ...COLLAPSE_GROUPS_BASE_CONFIG,
    type: SettingsType.BUTTON,
    label: labels.changeDepth,
    isActive: cappedTreeDepth < 2,
    isDisabled,
    onClick: () => {
      const settings = { treeDepth: cappedTreeDepth === 2 ? 1 : 2 };
      onViewSettingsUpdate(viewId, settings, true);
    },
    popoverText: isDisabled ? labels.noDepth : undefined,
  };
};

const collapseChildrenTreeDropdownSlider = ({
  viewId,
  treeDepth,
  maxTreeDepth,
  hasCollapsedNodes,
  onViewSettingsUpdate,
  labels,
}: ResolvedCollapseChildrenTreeArgs): DropdownConfig => ({
  ...COLLAPSE_GROUPS_BASE_CONFIG,
  label: labels.changeDepth,
  type: SettingsType.DROPDOWN,
  isKeepOpen: true,
  isActive: hasCollapsedNodes,
  maxDropdownSize: DropdownSize.FULL_WIDTH,
  options: [
    getDropdownSliderOption(
      viewId,
      onViewSettingsUpdate
    )({
      name: 'treeDepth',
      label: labels.collapse,
      rightLabel: labels.expand,
      value: hasCollapsedNodes ? treeDepth : Infinity,
      min: 1,
      max: maxTreeDepth,
      step: 1,
      substituteMax: Infinity,
      type: DropdownOptionType.SLIDER,
    }),
  ],
});

export const collapseChildrenDropdownTree = (
  args: CollapseChildrenTreeArgs
): ButtonConfig | DropdownConfig => {
  const resolvedArgs: ResolvedCollapseChildrenTreeArgs = {
    ...args,
    labels: { ...DEFAULT_LABELS, ...args.labels },
  };
  return resolvedArgs.maxTreeDepth < 3
    ? collapseChildrenTreeButton(resolvedArgs)
    : collapseChildrenTreeDropdownSlider(resolvedArgs);
};
